import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/v2/ui/dialog";
import { Button } from "@/components/v2/ui/button";
import { Input } from "@/components/v2/ui/input";
import { Label } from "@/components/v2/ui/label";
import { Phone, Loader2 } from "lucide-react";
import { BACKEND_API_ROUTE, POOJA_LEADS } from "@/commons/api_routes";
import { POSTREQUESTAPI_2 } from "@/pages/api/apiUtils";

const SESSION_KEY = "bmpo_exit_shown";
const GRACE_PERIOD_MS = 5000; // 5 seconds before any trigger can fire
const INACTIVITY_MS = 20000; // 20 seconds of inactivity
const FAST_SCROLL_UP_THRESHOLD = 150; // Pixels per scroll event that indicate "fast" up scroll
const VELOCITY_THRESHOLD = -0.2; // Speed of upward mouse movement (px/ms)
const TRIGGER_ZONE_Y = 300; // Only track speed if mouse is in the top 300px
const SCROLL_DEPTH_THRESHOLD = 0.7; // 70% of the page

const ExitIntentPopup = ({ popupData }) => {

  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const lastMousePosRef = useRef({ y: 0, time: 0 });
  const lastScrollYRef = useRef(0);
  const inactivityTimerRef = useRef(null);
  const hasTriggeredRef = useRef(false);

  const triggerPopup = () => {
    if (hasTriggeredRef.current || sessionStorage.getItem(SESSION_KEY) || open || !isReady) return;
    
    sessionStorage.setItem(SESSION_KEY, "1");
    hasTriggeredRef.current = true;
    setOpen(true);
  };

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (hasTriggeredRef.current) return;

    inactivityTimerRef.current = setTimeout(() => {
      if (isReady) triggerPopup();
    }, INACTIVITY_MS);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    
    // 1. Grace period timer
    const graceTimer = setTimeout(() => {
      setIsReady(true);
    }, GRACE_PERIOD_MS);

    const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // 2. Scroll Triggers (Depth & Fast Up)
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;
      const depth = (scrollY + windowHeight) / fullHeight;
      
      resetInactivityTimer();

      // Scroll Depth Check
      if (depth >= SCROLL_DEPTH_THRESHOLD) {
        triggerPopup();
      }

      // Fast Scroll Up Check
      const scrollSpeed = lastScrollYRef.current - scrollY;
      if (scrollSpeed > FAST_SCROLL_UP_THRESHOLD && scrollY > 500) {
        triggerPopup();
      }
      
      lastScrollYRef.current = scrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // 3. Activity Reset
    const handleActivity = () => resetInactivityTimer();
    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keydown", handleActivity);
    document.addEventListener("click", handleActivity);

    // Initial timer start
    resetInactivityTimer();

    // 4. Mouse Triggers (Desktop only)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        triggerPopup();
      }
    };

    const handleMouseMove = (e) => {
      const now = Date.now();
      const currentY = e.clientY;
      const dt = now - lastMousePosRef.current.time;
      const dy = currentY - lastMousePosRef.current.y;
      
      if (dt > 0 && dt < 100) { 
        const velocity = dy / dt;
        if (currentY < TRIGGER_ZONE_Y && velocity < VELOCITY_THRESHOLD) {
          triggerPopup();
        }
      }

      lastMousePosRef.current = { y: currentY, time: now };
    };

    if (!isMobile) {
      document.addEventListener("mouseleave", handleMouseLeave);
      document.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      clearTimeout(graceTimer);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      document.removeEventListener("click", handleActivity);
      
      if (!isMobile) {
        document.removeEventListener("mouseleave", handleMouseLeave);
        document.removeEventListener("mousemove", handleMouseMove);
      }
    };
  }, [isReady, open]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const name = id.replace("exit-", "");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      ...formData,
      source: "exit_intent_popup",
      pujaType: "General Enquiry",
      notes: "Customer was about to exit the page.",
    };

    const url = `${BACKEND_API_ROUTE}${POOJA_LEADS}/crm/generic`;

    try {
      await POSTREQUESTAPI_2(url, payload, {});
      setSubmitted(true);
    } catch (err) {
      console.error("Exit intent submission error:", err);
      setError("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md border-BluntRed/20 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif text-BluntRed">
            {submitted ? (popupData?.successTitle || "We'll Call You Soon! 🙏") : (popupData?.title || "Need more Guidance ?")}
          </DialogTitle>
          <DialogDescription className="text-base">
            {submitted
              ? (popupData?.successSubtitle || "Our Puja Seva team will reach out within 30 minutes to assist you.")
              : (popupData?.subtitle || "Get a free 1-on-1 consultation with our spiritual experts to find the perfect puja for your needs.")}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="exit-name" className="text-sm font-medium">Your Name</Label>
              <Input 
                id="exit-name" 
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your name" 
                required 
                className="border-primary/10" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="exit-phone" className="text-sm font-medium">Phone Number</Label>
              <Input
                id="exit-phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 98699 XXXXX"
                required
                className="border-primary/10"
              />
            </div>
            
            {error && <p className="text-xs text-destructive font-medium">{error}</p>}

            <Button 
              type="submit" 
              size="lg" 
              disabled={loading}
              className="w-full bg-BluntRed hover:bg-BluntRed/90 text-white shadow-lg transition-all active:scale-95"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  <Phone className="mr-2 h-4 w-4" /> {popupData?.buttonText || "Request Free Callback"}
                </>
              )}
            </Button>
            <p className="text-[10px] text-center text-muted-foreground uppercase tracking-wider">
              {popupData?.footerText || "No spam • Free consultation • Expert advice"}
            </p>
          </form>
        ) : (
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
              <Phone className="h-8 w-8" />
            </div>
            <p className="font-medium text-muted-foreground">Thank you for reaching out!</p>
            <Button variant="outline" onClick={() => setOpen(false)} className="w-full">
              Continue Browsing
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
